<template>
  <div class="container">
    <div v-if="showMainSearch" class="main-search">
      <span>{{ $t('checker.main_title') }}</span>
      <div class="all-search">
        <div class="search-container">
          <v-text-field
            v-model="searchInput"
            dense
            hide-details
            class="search-input"
            :placeholder="searchPlaceholder"
            @keyup.enter.native="handleSearch()"
          >
            <v-icon slot="append" @click="handleSearch">mdi-magnify</v-icon>
          </v-text-field>
          <div v-if="isSearching" class="search-progress">
            <div class="search-progress-fill" />
          </div>
        </div>

        <div class="ddd-upload-section">
          <div class="ddd-upload-container">
            <input
              id="ddd-file-input"
              ref="dddFileInput"
              type="file"
              accept=".ddd"
              multiple
              class="ddd-file-input"
              @change="handleDDDFileUpload"
            />
            <label for="ddd-file-input" class="ddd-upload-label">
              <v-icon>mdi-upload</v-icon>
              <span>{{ $t('checker.ddd_upload') }}</span>
            </label>
            <div v-if="isDddUploading" class="ddd-upload-progress">
              <div
                class="ddd-upload-progress-fill"
                :style="{ width: `${dddUploadProgress}%` }"
              />
              <div class="ddd-upload-progress-shimmer" />
            </div>
          </div>
          <!--eslint-disable @intlify/vue-i18n/no-raw-text-->
          <span v-if="isDddUploading" class="ddd-upload-count">
            {{ dddUploadCompleted }} / {{ dddUploadTotal }}
          </span>
          <!--eslint-enable @intlify/vue-i18n/no-raw-text-->
        </div>
      </div>
      <span class="usage">
        {{ $t('checker.usage') }}
      </span>
      <span class="hint">
        {{ $t('checker.search_hint') }}
        <a href="#" class="search-link" @click.prevent="showMoreOptions = true">
          {{ $t('checker.search_details') }}
        </a>
      </span>
    </div>

    <div v-if="showMoreOptions || missingPlates.length > 0" class="title">
      {{ $t('checker.details_title') }}
    </div>
    <div v-if="showMoreOptions" class="details-form-block">
      <v-icon class="close-icon" @click="showMoreOptions = false">mdi-close</v-icon>
      <CompatibilityForm @searchDetails="handleSearchDetails" />
    </div>
    <div v-if="missingPlates.length > 0" class="details-form-block">
      <v-icon class="close-icon" @click="missingPlates = []">mdi-close</v-icon>
      <CompatibilityForm
        v-for="plate in missingPlates"
        :key="plate"
        :plate="plate"
        :caseNotFound="true"
        @searchDetails="handleSearchDetails"
      />
    </div>
    <div v-if="vehicleInfoCompatibilities.length > 0" class="compatibility-table-block">
      <span class="actions">
        <div class="title">
          {{ $t('checker.vehicle_info_results_title') }}
        </div>
      </span>
      <VehicleInfoCompatibilityTable
        :compatibilities="vehicleInfoCompatibilities"
        :updatingRows="updatingVehicleInfoRows"
        @resubmitVehicleInfo="handleResubmitVehicleInfo"
      />
    </div>
    <div v-if="lpnCompatibilities.length > 0" class="compatibility-table-block">
      <span class="actions">
        <div class="title">
          {{ $t('checker.lpn_results_title') }}
        </div>
        <downloadCsv
          class="export-btn mx-6 my-2"
          :data="lpnCsvData"
          name="lpn-compatibilities.csv"
        >
          <span><v-icon>mdi-tray-arrow-down</v-icon>{{ $t('checker.export') }}</span>
        </downloadCsv>
      </span>
      <CompatibilityTable
        :compatibilities="lpnCompatibilities"
        type="lpn"
        @resolveBrandModel="handleResolveBrandModel"
      />
    </div>
    <div v-if="vinCompatibilities.length > 0" class="compatibility-table-block">
      <span class="actions">
        <div class="title">
          {{ $t('checker.vin_results_title') }}
        </div>
        <downloadCsv
          class="export-btn mx-6 my-2"
          :data="vinCsvData"
          name="vin-compatibilities.csv"
        >
          <span><v-icon>mdi-tray-arrow-down</v-icon>{{ $t('checker.export') }}</span>
        </downloadCsv>
      </span>
      <CompatibilityTable
        :compatibilities="vinCompatibilities"
        type="vin"
        @resolveBrandModel="handleResolveBrandModel"
      />
    </div>
    <div v-if="dddCompatibilities.length > 0" class="compatibility-table-block">
      <span class="actions">
        <div class="title">
          {{ $t('checker.ddd_results_title') }}
        </div>
        <downloadCsv
          class="export-btn mx-6 my-2"
          :data="dddCsvData"
          name="ddd-compatibilities.csv"
        >
          <span><v-icon>mdi-tray-arrow-down</v-icon>{{ $t('checker.export') }}</span>
        </downloadCsv>
      </span>
      <CompatibilityTable
        :compatibilities="dddCompatibilities"
        type="ddd"
      />
    </div>
    <Notification
      :content="notificationContent"
      :type="notificationStatus"
      :isNotificationOpen="isNotificationOpen"
      @notificationClosed="closeNotification"
    />
  </div>
</template>
<script setup lang="ts">
import { ref, computed } from 'vue';
import { CompatibilityRequest, CompatibilityResponse } from 'ctp-compatibility-checker';
import useStore from '@/utils/store/common';
import { useI18n } from 'vue-i18n-composable';
import Notification from '@/components/common/Notification/Notification.vue';
import downloadCsv from 'vue-json-csv';
import CompatibilityTable from './components/CompatibilityTable.vue';
import CompatibilityForm from './components/CompatibilityForm.vue';
import VehicleInfoCompatibilityTable from './components/VehicleInfoCompatibilityTable.vue';
import { CompatibilityTableType } from './components/tableColumns';
import getDddGeneration from './dddGeneration';
import { extractCompatibleNotifications } from './compatibilityNotifications';
import { VehicleInfoCompatibility, VehicleInfoRequest } from './vehicleInfo';
import {
  classifyIdentifiers,
  normalizeIdentifierForComparison,
  partitionCompatibilityResults
} from './identifierClassification';
import './styles/compatibility-checker.scss';

const store = useStore();
const { t } = useI18n();

const notificationContent = ref('');
const notificationStatus = ref('');
const isNotificationOpen = ref(false);

const showMainSearch = true;
const showMoreOptions = ref(false);
const lpnCompatibilities = ref<CompatibilityResponse[]>([]);
const vinCompatibilities = ref<CompatibilityResponse[]>([]);
const dddCompatibilities = ref<CompatibilityResponse[]>([]);
const vehicleInfoCompatibilities = ref<VehicleInfoCompatibility[]>([]);
const updatingVehicleInfoRows = ref<number[]>([]);
const missingPlates = ref<string[]>([]);
const isSearching = ref(false);
const dddFileName = ref('');
const dddFileInput = ref<HTMLInputElement | null>(null);
const isDddUploading = ref(false);
const dddUploadTotal = ref(0);
const dddUploadCompleted = ref(0);
const dddUploadProgress = computed(() => (
  dddUploadTotal.value > 0
    ? Math.round((dddUploadCompleted.value / dddUploadTotal.value) * 100)
    : 0
));

const openNotification = (type: 'warning' | 'error', content: string) => {
  notificationStatus.value = type;
  notificationContent.value = content;
  isNotificationOpen.value = true;
};

const getErrorMessage = async (error: unknown) => {
  const fallbackMessage = t('common.error.api_content').toString();

  try {
    const parsedError = await Promise.resolve(error);

    if (typeof parsedError === 'string') {
      return parsedError;
    }

    if (parsedError && typeof parsedError === 'object' && 'message' in parsedError) {
      const { message } = (parsedError as { message?: unknown });
      if (typeof message === 'string' && message.length > 0) {
        return message;
      }
    }
  } catch (_) {
    // Keep fallback message when error payload parsing fails.
  }

  return fallbackMessage;
};

const getLicencePlateExportValue = (licencePlate?: string | null) => {
  if (!licencePlate || licencePlate.includes('?')) {
    return '-';
  }

  return licencePlate;
};

const getCommonExportData = (compat: CompatibilityResponse) => ({
  'DTCO manufacturer': compat.dtcoData?.manufacturerCode,
  'DTCO version': compat.dtcoData?.firmwareVersion,
  'GNSS based tolling': compat.polandEToll ?? false,
  'Live Tracking': compat.liveTracking ?? false,
  'Remote Download': compat.remoteDownload ?? false,
  'EETS Tolling': compat.eetsTolling ?? ''
});

const lpnCsvData = computed(() => lpnCompatibilities.value.map(compat => ({
  'Licence Plate Number': getLicencePlateExportValue(compat.licencePlateNumber),
  'Vehicle Brand': compat.brand,
  'Vehicle Model': compat.model,
  ...getCommonExportData(compat)
})));

const vinCsvData = computed(() => vinCompatibilities.value.map(compat => ({
  'VIN': compat.vin,
  'Vehicle Brand': compat.brand,
  'Vehicle Model': compat.model,
  ...getCommonExportData(compat)
})));

const dddCsvData = computed(() => dddCompatibilities.value.map(compat => ({
  VIN: compat.vin,
  Generation: getDddGeneration(compat.compatibleNotifications),
  ...getCommonExportData(compat)
})));

// SEARCH BY DETAILS
const handleSearchDetails = async (details: CompatibilityRequest) => {
  const request = { ...details } as VehicleInfoRequest;
  const normalizedPlate = details.licencePlateNumber
    ? normalizeIdentifierForComparison(details.licencePlateNumber)
    : undefined;

  if (normalizedPlate) {
    missingPlates.value = missingPlates.value.filter(
      (plate) => {
        const normalized = normalizeIdentifierForComparison(plate);
        return normalized !== normalizedPlate;
      }
    );
  }

  try {
    const res = await store.dispatch('checker/checkVehicleInfoCompatibility', details);
    if (res) {
      vehicleInfoCompatibilities.value.push({ request, response: res });

      const notifications = extractCompatibleNotifications(res);
      if (notifications.length > 0) {
        openNotification('warning', notifications.join(' | '));
      }
    }
  } catch (error) {
    const message = await getErrorMessage(error);
    openNotification('error', message);
  }
};

const handleResubmitVehicleInfo = async (payload: {
  rowIndex: number;
  request: VehicleInfoRequest;
}) => {
  if (updatingVehicleInfoRows.value.includes(payload.rowIndex)) {
    return;
  }

  updatingVehicleInfoRows.value.push(payload.rowIndex);

  try {
    const res = await store.dispatch('checker/checkVehicleInfoCompatibility', payload.request);
    if (res) {
      vehicleInfoCompatibilities.value.splice(payload.rowIndex, 1, {
        request: payload.request,
        response: res
      });

      const notifications = extractCompatibleNotifications(res);
      if (notifications.length > 0) {
        openNotification('warning', notifications.join(' | '));
      }
    }
  } catch (error) {
    const message = await getErrorMessage(error);
    openNotification('error', message);
  } finally {
    updatingVehicleInfoRows.value = updatingVehicleInfoRows.value.filter(
      rowIndex => rowIndex !== payload.rowIndex
    );
  }
};

const handleResolveBrandModel = async (payload: {
  rowIndex: number;
  brand: string;
  model: string;
  compat: CompatibilityResponse;
  tableType: CompatibilityTableType;
}) => {
  try {
    const res = await store.dispatch('checker/checkCompatibilityByModel', {
      brand: payload.brand,
      model: payload.model
    });

    if (res) {
      let target = lpnCompatibilities;
      if (payload.tableType === 'vin') {
        target = vinCompatibilities;
      } else if (payload.tableType === 'ddd') {
        target = dddCompatibilities;
      }
      target.value.splice(payload.rowIndex, 1, {
        ...payload.compat,
        ...res,
        brand: payload.brand,
        model: payload.model
      });

      const notifications = extractCompatibleNotifications(res);
      if (notifications.length > 0) {
        openNotification('warning', notifications.join(' | '));
      }
    }
  } catch (error) {
    const message = await getErrorMessage(error);
    openNotification('error', message);
  }
};

// SEARCH BY DDD FILE(S)
// Each selected file is sent as its own independent request to the backend
// (in parallel), so one corrupted/invalid file doesn't block the others.
const handleDDDFileUpload = async (event: Event) => {
  const target = event.target as HTMLInputElement;
  const files = target.files ? Array.from(target.files) : [];

  if (files.length === 0) {
    return;
  }

  dddFileName.value = files.map(file => file.name).join(', ');
  isDddUploading.value = true;
  dddUploadTotal.value = files.length;
  dddUploadCompleted.value = 0;

  const results = await Promise.allSettled(
    files.map(file => store.dispatch('checker/checkCompatibilityDdd', file)
      .finally(() => {
        dddUploadCompleted.value += 1;
      }))
  );

  const successfulResponses: CompatibilityResponse[] = [];
  const errorMessages: string[] = [];

  await Promise.all(results.map(async (result, index) => {
    const fileName = files[index].name;

    if (result.status === 'fulfilled') {
      if (result.value) {
        successfulResponses.push(...(Array.isArray(result.value) ? result.value : [result.value]));
      }
      return;
    }

    let message = await getErrorMessage(result.reason);
    const status = result.reason && typeof result.reason === 'object' && 'status' in result.reason
      ? result.reason.status
      : undefined;
    if (status === 413) {
      message = t('checker.notification.ddd_file_too_large').toString();
    } else if (status === 400) {
      message = 'DDD file could be corrupted';
    }
    errorMessages.push(`${fileName}: ${message}`);
  }));

  if (successfulResponses.length > 0) {
    dddCompatibilities.value = successfulResponses;
  }

  if (errorMessages.length > 0) {
    openNotification('error', errorMessages.join(' | '));
  } else {
    const notifications = extractCompatibleNotifications(successfulResponses);
    if (notifications.length > 0) {
      openNotification('warning', notifications.join(' | '));
    }
  }

  // Reset file input so the same file(s) can be re-selected later
  dddFileName.value = '';
  if (dddFileInput.value) {
    dddFileInput.value.value = '';
  }

  isDddUploading.value = false;
  dddUploadTotal.value = 0;
  dddUploadCompleted.value = 0;
};

const searchInput = ref('');
const searchPlaceholder = computed(() => t('checker.search_identifiers').toString());

const hasNoCompatibilityData = (compatibility: CompatibilityResponse) => (
  compatibility.eetsTolling === null
  && compatibility.polandEToll === null
  && compatibility.liveTracking === null
  && compatibility.remoteDownload === null
);

const handleSearch = async () => {
  if (isSearching.value) {
    return;
  }

  const { payload, invalidIdentifiers } = classifyIdentifiers(searchInput.value);
  const { licencePlateNumbers = [], vinCodes = [] } = payload;

  if (licencePlateNumbers.length === 0 && vinCodes.length === 0 && invalidIdentifiers.length === 0) {
    return;
  }

  const warningMessages: string[] = [];
  if (invalidIdentifiers.length > 0) {
    warningMessages.push(
      t('checker.notification.invalid_identifiers', {
        identifiers: invalidIdentifiers.join(', ')
      }).toString()
    );
  }

  if (licencePlateNumbers.length === 0 && vinCodes.length === 0) {
    if (warningMessages.length > 0) {
      openNotification('warning', warningMessages.join(' | '));
    }
    return;
  }

  isSearching.value = true;

  try {
    const res = await store.dispatch('checker/checkCompatibilityBatch', payload);
    if (res) {
      const partitionedResults = partitionCompatibilityResults(res, licencePlateNumbers, vinCodes);
      if (licencePlateNumbers.length > 0) {
        lpnCompatibilities.value = partitionedResults.lpn;
      }
      if (vinCodes.length > 0) {
        vinCompatibilities.value = partitionedResults.vin;
      }

      const noDataEntries = res.filter(hasNoCompatibilityData);
      const submittedPlates = new Set(licencePlateNumbers.map(normalizeIdentifierForComparison));
      const submittedVins = new Set(vinCodes.map(normalizeIdentifierForComparison));

      missingPlates.value = noDataEntries
        .map((compatibility: CompatibilityResponse) => compatibility.licencePlateNumber)
        .filter((plate: string | undefined): plate is string => (
          Boolean(plate) && submittedPlates.has(normalizeIdentifierForComparison(plate || ''))
        ));

      if (missingPlates.value.length > 0) {
        warningMessages.push(
          t('checker.notification.unknown_plates', { plates: missingPlates.value.join(', ') }).toString()
        );
      }

      const missingVins = noDataEntries
        .map((compatibility: CompatibilityResponse) => compatibility.vin)
        .filter((vin: string | undefined): vin is string => (
          Boolean(vin) && submittedVins.has(normalizeIdentifierForComparison(vin || ''))
        ));

      if (missingVins.length > 0) {
        warningMessages.push(
          t('checker.notification.unknown_vins', { vins: missingVins.join(', ') }).toString()
        );
      }

      const compatibilityNotifications = extractCompatibleNotifications(res);
      if (compatibilityNotifications.length > 0) {
        warningMessages.push(compatibilityNotifications.join(' | '));
      }

      if (warningMessages.length > 0) {
        openNotification('warning', warningMessages.join(' | '));
      }
    }
  } catch (error) {
    const message = await getErrorMessage(error);
    openNotification('error', message);
  } finally {
    isSearching.value = false;
  }
};

function closeNotification() {
  isNotificationOpen.value = false;
  notificationContent.value = '';
  notificationStatus.value = '';
}

</script>
